import { useParams, Link } from "react-router-dom";
import { articles, categories, siteInfo } from "@/mocks/articles";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/base/ArticleCard";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <div className="pt-20 flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mx-auto mb-4">
              <i className="ri-file-unknow-line text-2xl"></i>
            </div>
            <h1 className="text-xl font-heading font-semibold text-foreground-950 mb-2">
              文章未找到
            </h1>
            <p className="text-foreground-600 mb-6">
              你寻找的文章可能已经被移除或不存在
            </p>
            <Link
              to="/"
              className="bg-primary-500 text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug && a.category === article.category)
    .slice(0, 3);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    author: {
      "@type": "Person",
      name: article.author,
    },
    datePublished: article.date,
    image: article.coverImage,
    publisher: {
      "@type": "Organization",
      name: siteInfo.name,
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">
        {JSON.stringify(articleSchema)}
      </script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-14">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                ...(category
                  ? [
                      {
                        label: category.name,
                        to: `/category/${category.slug}`,
                      },
                    ]
                  : []),
                { label: article.title },
              ]}
            />

            {/* Article Header */}
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="inline-block text-xs font-medium text-primary-600 bg-primary-50 px-2.5 py-1 rounded-md hover:bg-primary-100 transition-colors whitespace-nowrap"
                  >
                    {category.name}
                  </Link>
                )}
                <span className="text-xs text-foreground-500">
                  {article.date}
                </span>
                <span className="text-foreground-300 text-xs">·</span>
                <span className="text-xs text-foreground-500">
                  {article.readTime}
                </span>
              </div>

              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>

              <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
                {article.excerpt}
              </p>

              <div className="flex items-center gap-2 flex-wrap">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-block text-xs text-secondary-700 bg-secondary-50 px-2.5 py-1 rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="flex items-center gap-3 mt-5 pt-5 border-t border-background-200/70">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-medium text-sm">
                  {article.author.charAt(0)}
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-800">
                    {article.author}
                  </p>
                  <p className="text-xs text-foreground-500">作者</p>
                </div>
              </div>
            </header>

            {/* Cover Image */}
            <div className="relative w-full aspect-[16/9] rounded-xl overflow-hidden mb-10">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Article Content */}
            <article className="prose-custom">
              <div
                className="text-foreground-800 leading-relaxed text-base"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </article>

            {/* Article Footer */}
            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-foreground-600">标签：</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-block text-xs text-secondary-700 bg-secondary-50 px-2.5 py-1 rounded-full"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <section className="mt-14">
                <h3 className="text-xl font-heading font-bold text-foreground-950 mb-6">
                  相关文章
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((article) => (
                    <ArticleCard key={article.slug} article={article} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}